import cx from 'classnames';
import { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { NavigationWarningModal } from '@shared/components/modal/NavigationWarningModal';
import { Tooltip } from '@shared/components/tooltip/Tooltip';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { useAuth } from 'restaurantAdmin/context/AuthContext';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useFeatureFlagContext } from 'restaurantAdmin/featureFlags';
import {
  OPERATIONS_BUSY_TIMES_PATH,
  OPERATIONS_FORECAST_PATH,
  OPERATIONS_PRICING_GRID_PATH,
  OPERATIONS_TABLE_RATING_PATH,
} from 'restaurantAdmin/paths';
import typography from '~styles/typography.scss';
import styles from './PricingPageNavLinks.scss';

const ML_PRICING_TOOLTIP = 'Machine Learning Pricing Active';

interface PricingPageNavLinksProps {
  userHasUnsavedChanges: boolean;
  saveChanges: () => void;
  modalMessage: ReactNode;
}

export const PricingPageNavLinks = ({
  userHasUnsavedChanges,
  saveChanges,
  modalMessage,
}: PricingPageNavLinksProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMlPricingEnabled } = useRestaurant();
  const [targetNavigationLocation, setTargetNavigationLocation] = useState('');

  const {
    isOpen: isNavigationWarningModalOpen,
    open: openNavigationWarningModal,
    close: closeNavigationWarningModal,
  } = useIsOpen();

  const handleUserNavigation = (location: string) => {
    if (userHasUnsavedChanges) {
      setTargetNavigationLocation(location);
      openNavigationWarningModal();
    } else {
      navigate(location);
    }
  };

  const saveChangesAndNavigate = () => {
    saveChanges();
    closeNavigationWarningModal();
    navigate(targetNavigationLocation);
  };

  const loseChangesAndNavigate = () => {
    closeNavigationWarningModal();
    navigate(targetNavigationLocation);
  };

  const navLinkClassNames = ({
    isActive,
    isDisabled = false,
  }: {
    isActive: boolean;
    isDisabled?: boolean;
  }) =>
    cx({
      [typography.h7m]: true,
      [styles.navLink]: true,
      [styles.active]: isActive,
      [styles.disabled]: isDisabled,
    });

  const renderMlGatedLink = (
    label: string,
    path: string,
    isActive: boolean,
  ) => {
    if (isMlPricingEnabled) {
      return (
        <Tooltip content={ML_PRICING_TOOLTIP}>
          <button
            aria-disabled
            className={navLinkClassNames({ isActive, isDisabled: true })}
          >
            {label}
          </button>
        </Tooltip>
      );
    }
    return (
      <button
        className={navLinkClassNames({ isActive })}
        onClick={() => handleUserNavigation(path)}
      >
        {label}
      </button>
    );
  };

  return (
    <>
      {renderMlGatedLink(
        'Forecast',
        OPERATIONS_FORECAST_PATH,
        location.pathname === OPERATIONS_FORECAST_PATH ||
          location.pathname.startsWith(`${OPERATIONS_FORECAST_PATH}/`),
      )}
      {renderMlGatedLink(
        'Busy Times',
        OPERATIONS_BUSY_TIMES_PATH,
        location.pathname === OPERATIONS_BUSY_TIMES_PATH ||
          location.pathname.startsWith(`${OPERATIONS_BUSY_TIMES_PATH}/`),
      )}
      {renderMlGatedLink(
        'Table Rating',
        OPERATIONS_TABLE_RATING_PATH,
        location.pathname.includes(OPERATIONS_TABLE_RATING_PATH),
      )}
      <button
        className={navLinkClassNames({
          isActive: location.pathname.includes(OPERATIONS_PRICING_GRID_PATH),
        })}
        onClick={() => handleUserNavigation(OPERATIONS_PRICING_GRID_PATH)}
      >
        Pricing Grid
      </button>

      <NavigationWarningModal
        isOpen={isNavigationWarningModalOpen}
        onClose={closeNavigationWarningModal}
        modalMessage={modalMessage}
        handleSaveChangesClick={saveChangesAndNavigate}
        handleLoseChangesClick={loseChangesAndNavigate}
      />
    </>
  );
};
